import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Phone, ArrowRight, Truck, MapPin, Clock, AlertCircle, ShoppingBag } from 'lucide-react';
import { SHOP_URL } from '../lib/constants';

export default function DeliveryPage() {
  const [formData, setFormData] = useState({ name: '', phone: '', address: '', notes: '' });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Scroll to top when submission is successful
  useEffect(() => {
    if (submitted) {
      window.scrollTo(0, 0);
    }
  }, [submitted]);

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\+?\d{7,15}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Please enter a valid phone number (only + and digits)';
    }
    if (!formData.address.trim()) newErrors.address = 'Delivery address is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) {
      setSubmitError('Please complete all required fields correctly.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/api/delivery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitted(true);
      } else {
        setSubmitError('Please check the form and try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Please check the form and try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center py-20 bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50" style={{ minHeight: 'calc(100vh - 5rem)' }}>
        <div className="max-w-md mx-auto text-center px-4">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Request Sent!</h1>
          <p className="text-slate-600 mb-8">Your delivery request has been submitted successfully. We'll handle everything from here!</p>
          <Link to="/" className="bg-slate-900 text-white px-6 py-3 rounded-full font-medium hover:bg-slate-800">
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-slate-50 via-cyan-50/30 to-blue-50/50">
      {/* Hero */}
      <section className="py-12 md:py-16">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Truck className="w-4 h-4" />
            Always FREE
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Free Delivery <span className="text-slate-500">to Your Door</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            We deliver across all five NYC boroughs, absolutely free!
          </p>
        </div>
      </section>

      {/* Info Cards */}
      <section className="container-custom mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 text-center">
            <MapPin className="w-8 h-8 text-blue-600 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">All 5 Boroughs</h3>
            <p className="text-sm text-slate-500">Manhattan, Brooklyn, Queens, Bronx, Staten Island</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-100 text-center">
            <Truck className="w-8 h-8 text-green-600 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">$0 Delivery Fee</h3>
            <p className="text-sm text-slate-500">No minimum order required</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border border-slate-100 text-center">
            <Clock className="w-8 h-8 text-purple-600 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">Same-Day Available</h3>
            <p className="text-sm text-slate-500">Priority Red Hook service</p>
          </div>
        </div>
      </section>

      {/* Order Online Too */}
      <section className="container-custom mb-12">
        <div className="max-w-4xl mx-auto bg-slate-900 rounded-3xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-center md:text-left flex-col md:flex-row">
            <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-7 h-7 text-white" />
            </div>
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-white mb-1">Prefer to order online?</h3>
              <p className="text-slate-400 text-sm">Shop prescriptions, OTC and daily essentials from our online store — same free delivery applies.</p>
            </div>
          </div>
          <a
            href={SHOP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-medium text-sm hover:opacity-90 transition-opacity flex items-center gap-2 whitespace-nowrap flex-shrink-0"
            data-testid="delivery-shop-online-button"
          >
            <ShoppingBag className="w-4 h-4" />
            Shop Online
          </a>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-xl mx-auto bg-white p-8 rounded-3xl shadow-lg">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6 text-center">Request a Delivery</h2>
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => { setFormData({ ...formData, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.name ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="Enter your name"
                />
                {errors.name && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => { const val = e.target.value.replace(/[^+\d]/g, ''); setFormData({ ...formData, phone: val }); if (errors.phone) setErrors({ ...errors, phone: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.phone ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="(555) 555-5555"
                />
                {errors.phone && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.phone}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Delivery Address *</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => { setFormData({ ...formData, address: e.target.value }); if (errors.address) setErrors({ ...errors, address: '' }); }}
                  className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 ${errors.address ? 'border-red-400 bg-red-50/50' : 'border-slate-200'}`}
                  placeholder="Enter your full address"
                />
                {errors.address && <p className="mt-1.5 text-sm text-red-500 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" />{errors.address}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Notes (optional)</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  rows={3}
                  placeholder="Gate code, buzzer info, etc."
                />
              </div>
              {submitError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-center">
                  <p className="text-sm text-red-600 flex items-center justify-center gap-1.5">
                    <AlertCircle className="w-4 h-4" />{submitError}
                  </p>
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-4 rounded-full font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Request Free Delivery'}
                {!loading && <ArrowRight className="w-5 h-5" />}
              </button>
              <p className="text-center text-sm text-green-600 font-medium">
                Delivery is always FREE!
              </p>
            </form>
          </div>

          {/* Call CTA */}
          <div className="mt-8 text-center">
            <p className="text-slate-600 mb-3">Need it urgently? Call us directly.</p>
            <a href="tel:+17187970200" className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-900 px-6 py-3 rounded-full font-medium hover:bg-slate-50">
              <Phone className="w-4 h-4" />
              (718) 797-0200
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
